import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PiTrashLight, PiUserListLight, PiProhibit, PiPlusLight, PiUploadLight, PiClockCounterClockwiseLight } from 'react-icons/pi';
import Button from '../shared/TailwindComponents/Button';
import Alert from '../shared/TailwindComponents/Alert';
import ResourceTrash from '../RecycleBin/ResourceTrash';
import DeactivatedContacts from './DeactivatedContacts';

import { listClients, deleteClient as deleteClientAPI, bulkDeleteClients, Client } from '../../store/actions/ClientActions';
import ImportClientsWizard from '../Import/ImportClientsWizard';
import DeleteClientsConfirmation from './DeleteClientsConfirmation';
import Header from '../shared/TailwindComponents/Header';
import Badge from '../shared/TailwindComponents/Badge';
import Table from '../shared/TailwindComponents/Table';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import ClientForm from './ClientForm';
import apis from '../../utils/apis';
import { useAppDispatch } from '../../store';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import { FeatureTour, clientsListTourSteps } from '../shared/FeatureTour';
import { useFeatureTour } from '../../hooks/useFeatureTour';

import ActivityDrawer from '../shared/activity/ActivityDrawer';
import CollaboratorStack from '../shared/access/CollaboratorStack';
import { contactTypeBadge, parentLabelsByTypeId } from './contactTypeBadge';

/**
 * The column header the user presses is not the field the API sorts on.
 * `sortBy` holds the API's spelling (it is what `listClients` receives), so a
 * header press is translated on the way in and the state back out on the way
 * to `Table`. Pressing the old "Client" header sent `sort_by=Client`, which
 * matched no backend field.
 */
const SORT_FIELD_BY_COLUMN: Record<string, string> = {
  Name: 'name',
  Matters: 'matters_count',
};
const SORT_COLUMN_BY_FIELD: Record<string, string> = {
  name: 'Name',
  matters_count: 'Matters',
};

const isLocalhost = () => window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const ClientsList: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isMountedRef = useRef(true);

  // Local state only
  const [clients, setClients] = useState<Client[]>([]);
  const [totalClients, setTotalClients] = useState(0);
  const [isAddingClient, setIsAddingClient] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [trashCount, setTrashCount] = useState<{ showing: number; total: number }>({ showing: 0, total: 0 });
  const [deactivatedCount, setDeactivatedCount] = useState(0);
  const [trashBulkActions, setTrashBulkActions] = useState<{ label: string; onClick: () => void; icon?: React.ReactElement }[]>([]);
  const [trashSelectedCount, setTrashSelectedCount] = useState(0);
  const [isClientsLoading, setIsClientsLoading] = useState(false);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [clientTypeMap, setClientTypeMap] = useState<Record<number, string>>({});
  /**
   * type_id → the label of its PARENT type - the family the Type badge is
   * coloured by. This is the fallback for rows that predate the backend's
   * `parent_type_code`; the row's own field wins when it is there. See
   * `contactTypeBadge`.
   */
  const [parentTypeMap, setParentTypeMap] = useState<Record<number, string>>({});
  const [hierarchicalClientTypes, setHierarchicalClientTypes] = useState<any[]>([]);
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});
  const [isImporting, setIsImporting] = useState(false);
  const [auditTarget, setAuditTarget] = useState<{ type: string; id: string; name: string } | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const clientsPerPage = 50;

  const {
    isRunning: isTourRunning,
    handleJoyrideCallback: handleTourCallback,
  } = useFeatureTour({
    tourId: 'clients-list',
    steps: clientsListTourSteps,
    readyToShow: !isClientsLoading || clients.length > 0,
    autoStartDelay: 600,
  });

  // Build hierarchical client types from flat list
  const buildHierarchicalClientTypes = (flatList: any[]): any[] => {
    const typeMap = new Map<number | string, any>();

    // First pass: create all items
    flatList.forEach((item: any) => {
      typeMap.set(item.id, {
        value: String(item.id),
        label: item.label,
        id: item.id,
        parent_id: item.parent_id,
        sort_order: item.sort_order,
        children: [],
      });
    });

    // Second pass: build hierarchy
    const roots: any[] = [];
    typeMap.forEach((option, itemId) => {
      if (option.parent_id === null) {
        roots.push(option);
      } else {
        const parent = typeMap.get(option.parent_id as number);
        if (parent && parent.children) {
          parent.children.push(option);
        }
      }
    });

    // Sort by sort_order
    roots.sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
    roots.forEach((parent: any) => {
      if (parent.children) {
        parent.children.sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
      }
    });

    return roots;
  };

  // Sync URL with isAddingClient state
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get('add') === 'true') {
      setIsAddingClient(true);
    } else {
      setIsAddingClient(false);
    }
  }, [location]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fetch client types from dropdown
  useEffect(() => {
    const fetchClientTypes = async () => {
      try {
        const response = await apis.get('/dropdown/client-types');
        if (response && response.data) {
          let typeList = Array.isArray(response.data) ? response.data : response.data.data || response.data.list || [];

          // Build flat map for display purposes
          const typeMap: Record<number, string> = {};
          typeList.forEach((item: any) => {
            const itemId = item.id;
            const itemName = item.label;
            if (itemId && itemName) {
              const numId = typeof itemId === 'string' ? parseInt(itemId, 10) : itemId;
              if (!isNaN(numId)) {
                typeMap[numId] = itemName;
              }
            }
          });

          if (Object.keys(typeMap).length > 0) {
            setClientTypeMap(typeMap);
          }

          setParentTypeMap(parentLabelsByTypeId(typeList));

          // Build hierarchical structure for filters
          const hierarchical = buildHierarchicalClientTypes(typeList);
          setHierarchicalClientTypes(hierarchical);
        }
      } catch (err) {
        console.error('Error fetching client types:', err);
      }
    };

    fetchClientTypes();
  }, []);

  // Fetch clients (page 1, replaces list) - extracted to a memoized function so it can be reused
  const fetchClients = useCallback(async (sort_by?: string, sort_order?: 'asc' | 'desc', filters?: Record<string, string[]>) => {
    try {
      setIsClientsLoading(true);
      setError(null);
      setPage(1);
      setSelectedClients([]);

      const response = await listClients(1, clientsPerPage, {
        sort_by: sort_by || sortBy,
        sort_order: sort_order || sortOrder,
        filters: filters || selectedFilters,
        // Deactivated contacts live in the Archived view (DeactivatedContacts).
        is_active: true,
      });

      if (response && response.success && Array.isArray(response.clients)) {
        setClients(response.clients);
        const total = response.pagination?.total || 0;
        const totalPages = response.pagination?.total_pages || Math.ceil(total / clientsPerPage);
        setTotalClients(total);
        setHasMore(1 < totalPages && response.clients.length >= clientsPerPage);
      } else {
        throw new Error(`Invalid response: success=${response?.success}, clients=${Array.isArray(response?.clients)}`);
      }
    } catch (err: any) {
      if (!isMountedRef.current) return;
      const errorMsg = err?.message || t('Failed to fetch contacts');
      setError(errorMsg);
      setClients([]);
      setTotalClients(0);
      setHasMore(false);
    } finally {
      setIsClientsLoading(false);
    }
  }, [sortBy, sortOrder, selectedFilters]);

  // Load next page of clients (appends to current list)
  const loadMoreClients = useCallback(async () => {
    if (!hasMore || isClientsLoading) return;
    try {
      setIsClientsLoading(true);
      const nextPage = page + 1;

      const response = await listClients(nextPage, clientsPerPage, {
        sort_by: sortBy,
        sort_order: sortOrder,
        filters: selectedFilters,
        is_active: true,
      });

      if (response && response.success && Array.isArray(response.clients)) {
        setClients(prev => [...prev, ...response.clients]);
        setPage(nextPage);
        const total = response.pagination?.total || 0;
        const totalPages = response.pagination?.total_pages || Math.ceil(total / clientsPerPage);
        setTotalClients(total);
        setHasMore(nextPage < totalPages && response.clients.length >= clientsPerPage);
      }
    } catch (err: any) {
      if (!isMountedRef.current) return;
      setError(err?.message || t('Failed to load more contacts'));
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsClientsLoading(false);
    }
  }, [hasMore, isClientsLoading, page, sortBy, sortOrder, selectedFilters]);

  // Fetch clients on mount
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Handle client creation - navigate to client detail page
  const handleClientCreated = useCallback(async (newClient: any) => {
    // Navigate to the new client's detail page
    if (newClient && newClient.uuid) {
      history.push(`/contacts/${newClient.uuid}`);
    } else {
      // Fallback if uuid is not available
      await fetchClients();
      history.push('/contacts');
    }
  }, [fetchClients, history]);

  // Handle delete
  const handleDeleteClient = (clientId: string | number) => {
    const client = clients.find((c) => String(c.id) === String(clientId));
    if (!client) return;

    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        title: t('Delete contact'),
        message: t(`This will permanently delete "${client.name}". This action cannot be undone.`),
        variant: 'danger',
        icon: <PiTrashLight className='w-6 h-6 text-danger-600' />,
        confirmLabel: t('Delete'),
        onConfirm: async () => {
          try {
            await deleteClientAPI(client.uuid);

            if (!isMountedRef.current) return;

            // Re-fetch the entire list to ensure consistency
            await fetchClients();

            // Close the modal
            dispatch(hideModal());

          dispatch(
            showFeedbackModal({
              showModal: true,
              message: t('Contact deleted successfully'),
              modalType: 'success',
              duration: 3000,
            })
          );
          } catch (err: any) {
            if (!isMountedRef.current) return;

            const errorMsg = err?.message || t('Failed to delete contact');
            setError(errorMsg);
            setTimeout(() => setError(null), 5000);
          }
        },
        onCancel: () => {
          dispatch(hideModal());
        },
      } as any)
    );
  };

  // Handle bulk deleting selected clients - single API call with red spinner
  const handleBulkDeleteClients = () => {
    if (selectedClients.length === 0) {
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'warning',
        message: t('Please select at least one contact'),
        duration: 3000,
      }));
      return;
    }

    const clientUuids = clients
      .filter((c) => selectedClients.includes(String(c.id)))
      .map((c) => c.uuid);

    dispatch(
      showModal({
        showModal: true,
        type: 'custom',
        message: t(clientUuids.length > 1 ? 'Delete Contacts' : 'Delete Contact'),
        subMsg: '',
        icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
        content: (
          <DeleteClientsConfirmation
            clientCount={clientUuids.length}
            onConfirm={async () => {
              try {
                const result = await bulkDeleteClients(clientUuids);
                if (result && result.deleted_count > 0) {
                  await fetchClients();
                  setSelectedClients([]);
                  dispatch(showFeedbackModal({
                    message: t('{{count}} contact(s) deleted successfully', { count: result.deleted_count }),
                    modalType: result.failed_count > 0 ? 'warning' : 'success',
                    duration: 3000,
                    showModal: true,
                  }));
                }
                if (result?.failed_count > 0) {
                  setError(t('Failed to delete {{count}} contact(s)', { count: result.failed_count }));
                  setTimeout(() => setError(null), 5000);
                }
              } catch (error: any) {
                setError(error.message || t('Failed to delete contacts'));
                setTimeout(() => setError(null), 5000);
              }
              dispatch(hideModal());
            }}
            onCancel={() => dispatch(hideModal())}
          />
        ),
        onCancel: () => {},
      })
    );
  };

  // Handle deactivating selected clients
  const handleDeactivateClients = () => {
    if (selectedClients.length === 0) {
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'warning',
        message: t('Please select at least one contact'),
        duration: 3000,
      }));
      return;
    }

    const count = selectedClients.length;

    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        title: t('Deactivate contacts'),
        message: t(`Are you sure you want to deactivate ${count} selected contact(s)? They will no longer appear as active.`),
        variant: 'danger',
        icon: <PiProhibit className="w-6 h-6 text-danger-600" />,
        confirmLabel: t('Deactivate'),
        onConfirm: async () => {
          try {
            const clientsToDeactivate = clients.filter((c) =>
              selectedClients.includes(String(c.id))
            );

            const results = await Promise.allSettled(
              clientsToDeactivate.map((client) =>
                apis.put(`/clients/${client.uuid}`, { is_active: false })
              )
            );

            const failed = results.filter((r) => r.status === 'rejected').length;
            const succeeded = results.filter((r) => r.status === 'fulfilled').length;

            if (succeeded > 0) {
              setClients((prev) =>
                prev.map((c) =>
                  selectedClients.includes(String(c.id)) ? { ...c, is_active: false } : c
                )
              );
              dispatch(showFeedbackModal({
                message: t(`${succeeded} contact(s) deactivated successfully`),
                modalType: 'success',
                duration: 3000,
                showModal: true,
              }));
              setSelectedClients([]);
            }

            if (failed > 0) {
              setError(t(`Failed to deactivate ${failed} contact(s)`));
              setTimeout(() => setError(null), 5000);
            }
          } catch (error: any) {
            setError(error.message || t('Failed to deactivate contacts'));
            setTimeout(() => setError(null), 5000);
          }
          dispatch(hideModal());
        },
        onCancel: () => dispatch(hideModal()),
      })
    );
  };

  // Handle selecting/deselecting a single client
  const handleSelectItem = (id: string) => {
    setSelectedClients((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Handle selecting/deselecting all clients
  const handleSelectAllItems = () => {
    const allClientIds = clients.map((c) => String(c.id));
    const allSelected = selectedClients.length === allClientIds.length;
    setSelectedClients(allSelected ? [] : allClientIds);
  };

  // Handle sorting
  const handleSort = useCallback((column: string) => {
    const field = SORT_FIELD_BY_COLUMN[column] || column;
    let newSortOrder: 'asc' | 'desc' = 'asc';

    // If clicking the same column, toggle the sort order
    if (sortBy === field) {
      newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }

    setSortBy(field);
    setSortOrder(newSortOrder);

    // Fetch clients with new sort parameters
    fetchClients(field, newSortOrder);
  }, [sortBy, sortOrder, fetchClients]);

  // Handle filter changes
  const handleFilterChange = useCallback((filterSection: string, updatedFilters: string[]) => {
    const newSelectedFilters = { ...selectedFilters };

    if (updatedFilters.length > 0) {
      newSelectedFilters[filterSection] = updatedFilters;
    } else {
      delete newSelectedFilters[filterSection];
    }

    setSelectedFilters(newSelectedFilters);

    // Fetch clients with new filter parameters
    fetchClients(sortBy, sortOrder, newSelectedFilters);
  }, [selectedFilters, sortBy, sortOrder, fetchClients]);

  // Filter actions that appear when items are selected
  const filterActions = [
    ...(selectedClients.length > 0 ? [
      {
        label: t('Deactivate'),
        onClick: handleDeactivateClients,
        icon: <PiProhibit className="h-4 w-4" />,
      },
      {
        label: t('Delete'),
        onClick: handleBulkDeleteClients,
        icon: <PiTrashLight className="h-4 w-4" />,
      },
    ] : []),
  ];


  /**
   * A row per contact.
   *
   * WHAT CHANGED. There was one composite `Client` cell holding
   * `{ name, email, clientType }`, and for a company the "email" line was
   * literally `"Contact: {person}"` - so the entity's name and a DIFFERENT
   * human's name were stacked in one cell, on one row, under one header. Two
   * things, two columns: **Name** is the record, **Contact Person** is the
   * human you call about it, **Email** is how you reach the record.
   *
   * Type is a coloured badge grouped by the parent type rather than an icon
   * (see `contactTypeBadge`), and **Access** is the shared avatar stack the
   * projects and matters tables use, so "who else can see this contact" is
   * answered in the list rather than only inside the record.
   */
  const tableData = clients.map((client: any) => {
    const isIndividual = client.entity_kind
      ? client.entity_kind === 'person'
      : client.is_individual !== false;
    const typeFallback = clientTypeMap[client.type_id] || t('Unspecified');
    const badge = contactTypeBadge(
      {
        typeLabel: client.type_label || clientTypeMap[client.type_id],
        parentCode: client.parent_type_code,
        parentLabel: client.parent_type_label || parentTypeMap[client.type_id],
        entityKind: client.entity_kind || (isIndividual ? 'person' : 'business'),
      },
      typeFallback
    );
    // `has_access` is the backend's roster (feature/final-revamp-backend); the
    // dash is what a contact with nobody on it - or a backend without the
    // field yet - reads as, rather than an empty cell that looks broken.
    const access = Array.isArray(client.has_access) ? client.has_access : [];

    return {
      id: String(client.id),
      uuid: client.uuid,
      Name: (
        <span className="text-fg-strong truncate" title={client.name}>
          {client.name}
        </span>
      ),
      'Contact Person': client.contact_person_name ? (
        <span className="text-sm text-fg-body truncate" title={client.contact_person_name}>
          {client.contact_person_name}
        </span>
      ) : (
        <span className="text-xs text-fg-muted">—</span>
      ),
      Email: client.contact_email ? (
        <span className="text-sm text-fg-body truncate" title={client.contact_email}>
          {client.contact_email}
        </span>
      ) : (
        <span className="text-xs text-fg-muted">—</span>
      ),
      Type: <Badge color={badge.color}>{badge.label}</Badge>,
      Matters: <span className="text-sm text-fg-body">{client.matters_count || 0}</span>,
      Access: access.length > 0 ? (
        <CollaboratorStack collaborators={access} max={4} totalCount={client.access_count} />
      ) : (
        <span className="text-xs text-fg-muted">—</span>
      ),
      Status: (
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge color={client.is_active ? 'green' : 'gray'}>
            {client.is_active ? t('Active') : t('Inactive')}
          </Badge>
          {client.is_onboarded && <Badge color="blue">{t('Onboarded')}</Badge>}
        </div>
      ),
      name: client.name,
      client_type: badge.label,
      case_count: client.matters_count || 0,
      total_value: 0,
      className: selectedClients.includes(String(client.id))
        ? 'bg-info-50 dark:bg-info-900/20 hover:bg-info-100 dark:hover:bg-info-900/30'
        : '',
    };
  });

  const filters = [
    {
      id: 'type',
      name: t('Type'),
      options: hierarchicalClientTypes,
    },
  ];

  const columns = ['Name', 'Contact Person', 'Email', 'Type', 'Matters', 'Access', 'Status', 'Actions'];

  const sortableColumns = ['Name', 'Matters'];

  return (
    <div className="bg-bg text-fg-body transition-colors duration-300 flex flex-col h-full overflow-hidden">
      {/* Error message */}
      {error && (
        <div className="m-4">
          <Alert type="error" message={error} onClose={() => setError(null)} />
        </div>
      )}

      {/* `min-h-0`: a column flex item defaults to `min-height:auto`, which is
          content-based and quietly beats `flex-1` - so this box grew to its
          content and the table below it never had a measured height to fill. */}
      <div className="transition-all duration-200 bg-bg flex-1 min-h-0 flex flex-col">
        {!isAddingClient && (
          <>
            <Header
              actions={[
                // Import is only available on localhost until fully ready
                ...(isLocalhost() ? [{
                  label: t('Import contacts'),
                  icon: <PiUploadLight className='w-4 h-4' />,
                  onClick: () => setIsImporting(true),
                  variant: 'outline' as const,
                }] : []),
                {
                  label: t('Add contact'),
                  icon: <PiPlusLight className='w-4 h-4' />,
                  onClick: () => history.push('/contacts?add=true'),
                  requiresSubscription: true,
                  variant: 'primary' as const,
                },
              ]}
            />
            <div data-tour-id="clients-toolbar">
            <FilterBarWithTabs
              itemCount={showTrash ? trashCount.showing + deactivatedCount : clients.length}
              totalItemCount={showTrash ? trashCount.total + deactivatedCount : totalClients}
              itemCountLabel="contacts"
              listLoading={isClientsLoading && clients.length === 0}
              filters={filters}
              selectedFilters={selectedFilters}
              onFilterChange={handleFilterChange}
              onSearchChange={() => {}}
              onClearAllFilters={() => {
                setSelectedFilters({});
                fetchClients(sortBy, sortOrder, {});
              }}
              actions={[
                ...filterActions,
                ...(showTrash ? trashBulkActions : []),
              ]}
              selectionCount={showTrash ? trashSelectedCount : (selectedClients.length > 0 ? selectedClients.length : undefined)}
              recycleBinEnabled
              recycleBinActive={showTrash}
              recycleBinLabel={t('Contacts')}
              onRecycleBinToggle={(active) => { if (!active) fetchClients(sortBy, sortOrder, selectedFilters); setShowTrash(active); }}
            />
            </div>
          </>
        )}

        {showTrash ? (
          <div className="flex-grow min-h-0 flex flex-col">
          <DeactivatedContacts
            onCountChange={setDeactivatedCount}
            onReactivated={() => fetchClients(sortBy, sortOrder, selectedFilters)}
          />
          <ResourceTrash
            entityType="client"
            entityLabel="contacts"
            emptyIcon={<PiUserListLight />}
            onItemRestored={() => fetchClients(sortBy, sortOrder, selectedFilters)}
            onBulkActionsChange={setTrashBulkActions}
            onSelectedCountChange={setTrashSelectedCount}
            onCountChange={(showing, total) => setTrashCount({ showing, total })}
          />
          </div>
        ) : (
        <div
          // This host never scrolls: in add-client mode the form owns its own
          // scrolling + pinned footer, and in list mode the Table owns its own
          // scroll region (sticky header + scrollable body). Letting the host
          // scroll too produced the double scrollbar. Keep it `overflow-hidden`
          // and hand the full height down the flex chain to the single child.
          className="flex-grow min-h-0 flex flex-col overflow-hidden"
          data-tour-id="clients-table"
        >
          {isAddingClient ? (
            <div className="flex-grow min-h-0 flex flex-col">
              <ClientForm onCancel={() => history.push('/contacts')} onClientCreated={handleClientCreated} />
            </div>
          ) : (
            <div className="flex-grow min-h-0 flex flex-col">
              {(
                // Table renders its own built-in skeleton while loading
                <Table
                  isLoading={isClientsLoading && clients.length === 0}
                  columns={columns}
                  data={tableData}
                  enableSelection={true}
                  selectedIds={selectedClients}
                  onSelect={(id) => handleSelectItem(String(id))}
                  onSelectAll={handleSelectAllItems}
                  onRowClick={(row) => history.push(`/contacts/${row.uuid}`)}
                  actions={[
                    {
                      label: t('Activity'),
                      onClick: (row) => setAuditTarget({ type: 'client', id: row.uuid || row.id, name: row.name || '' }),
                      icon: <PiClockCounterClockwiseLight className="h-4 w-4" />,
                    },
                    {
                      label: t('Delete'),
                      onClick: (row) => handleDeleteClient(row.id),
                      icon: <PiTrashLight className="h-4 w-4" />,
                    },
                  ]}
                  hasMore={hasMore}
                  isLoadingMore={isClientsLoading && page > 1}
                  onLoadMore={loadMoreClients}
                  sortableColumns={sortableColumns}
                  sortBy={SORT_COLUMN_BY_FIELD[sortBy] || sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                  total_items={totalClients}
                  total_showing_items={clients.length}
                  setIsShowing={true}
                  showingLabel={t('contacts')}
                  selectedCount={selectedClients.length}
                  resizableColumns={true}
                  storageKeyPrefix="contacts"
                  defaultColumnWidths={{
                    'Name': 240,
                    'Contact Person': 180,
                    'Email': 220,
                    'Type': 160,
                    'Matters': 100,
                    'Access': 150,
                    'Status': 160,
                    'Actions': 48,
                  }}
                  emptyState={{
                    icon: <PiUserListLight />,
                    headline: t('You have no contacts added yet.'),
                    subtext: t('Add contacts — clients, opposing parties, external counsel, vendors — to keep their details, matters and documents in one place.'),
                    button: {
                      label: t('Add contact'),
                      onClick: () => history.push('/contacts?add=true'),
                    },
                  }}
                />
              )}
            </div>
          )}
        </div>
        )}
      </div>
      <ImportClientsWizard
        isOpen={isImporting}
        onClose={() => setIsImporting(false)}
        onComplete={() => fetchClients()}
      />
      <FeatureTour
        steps={clientsListTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />
      {auditTarget && (
        <ActivityDrawer onClose={() => setAuditTarget(null)}>
          <AuditLogPanel
            entityType={auditTarget.type}
            entityId={auditTarget.id}
            entityName={auditTarget.name}
            onClose={() => setAuditTarget(null)}
          />
        </ActivityDrawer>
      )}
    </div>
  );
};

export default ClientsList;
